
import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { VariantMeasurementList } from "./VariantMeasurementList";
import { VariantMeasurementDialog } from "./VariantMeasurementDialog";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";
import { SearchBar } from "@/components/shared/SearchBar";
import { useVariantMeasurements, VariantMeasurement, VariantMeasurementFormValues } from "@/hooks/useVariantMeasurements";
import { useProducts } from "@/hooks/useProducts";

export function VariantMeasurementManager() {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMeasurement, setEditingMeasurement] = useState<VariantMeasurement | null>(null);
  const [measurementsList, setMeasurementsList] = useState<VariantMeasurement[]>([]);
  const [filteredMeasurements, setFilteredMeasurements] = useState<VariantMeasurement[]>([]);
  const {
    variantMeasurements,
    loading,
    fetchVariantMeasurements,
    createVariantMeasurement,
    updateVariantMeasurement,
    deleteVariantMeasurement
  } = useVariantMeasurements();
  const {
    products
  } = useProducts();

  useEffect(() => {
    fetchVariantMeasurements();
  }, []);

  useEffect(() => {
    if (variantMeasurements) {
      setMeasurementsList(variantMeasurements);
      setFilteredMeasurements(variantMeasurements);
    }
  }, [variantMeasurements]);

  const handleSearch = (query: string) => {
    if (!query) {
      setFilteredMeasurements(measurementsList);
      return;
    }
    const normalizedQuery = query.toLowerCase();
    const filtered = measurementsList.filter(measurement => {
      return measurement.part_name.toLowerCase().includes(normalizedQuery) || measurement.description && measurement.description.toLowerCase().includes(normalizedQuery);
    });
    setFilteredMeasurements(filtered);
  };

  const handleSubmit = async (values: VariantMeasurementFormValues) => {
    try {
      let success;
      if (editingMeasurement) {
        success = await updateVariantMeasurement(editingMeasurement.id, values);
      } else {
        success = await createVariantMeasurement(values);
      }
      if (success) {
        setOpenDialog(false);
        setEditingMeasurement(null);
        fetchVariantMeasurements();
        return true;
      }
      return false;
    } catch (error: any) {
      console.error("Error in handleSubmit:", error);
      return false;
    }
  };

  const handleEdit = (measurement: VariantMeasurement) => {
    setEditingMeasurement(measurement);
    setOpenDialog(true);
  };

  const handleAddNew = () => {
    setEditingMeasurement(null);
    setOpenDialog(true);
  };

  const handleDelete = async (measurement: VariantMeasurement) => {
    const success = await deleteVariantMeasurement(measurement);
    if (success) {
      // Immediately update UI by filtering out the deleted measurement
      const updated = measurementsList.filter(m => m.id !== measurement.id);
      setMeasurementsList(updated);
      setFilteredMeasurements(updated);
    }
    return success;
  };

  const defaultValues: VariantMeasurementFormValues = {
    part_name: editingMeasurement?.part_name || "",
    measurement_value: editingMeasurement?.measurement_value || 0,
    tolerance: editingMeasurement?.tolerance || null,
    notes: editingMeasurement?.notes || "",
    description: editingMeasurement?.description || "",
    products: editingMeasurement?.products || [],
    factories: editingMeasurement?.factories || [],
    is_required: editingMeasurement?.is_required || false,
    measurement_order: editingMeasurement?.measurement_order || 0
  };

  return (
    <div className="space-y-8">
      <Card className="glass-card p-6 animate-fadeIn">
        <h2 className="text-2xl font-semibold mb-4">Measurements</h2>
        <p className="text-muted-foreground">
          Manage measurements for different variant parts.
        </p>
      </Card>

      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Measurements</h3>
          <div className="flex items-center gap-2">
            <SearchBar placeholder="Search measurements..." onSearch={handleSearch} className="w-auto" />
            <Button onClick={handleAddNew} className="flex items-center gap-2">
              <PlusCircle className="h-4 w-4" />
              Add Measurement
            </Button>
          </div>
        </div>
        <VariantMeasurementList measurements={filteredMeasurements} isLoading={loading} onAdd={handleAddNew} onEdit={handleEdit} onDelete={handleDelete} />
      </Card>

      <VariantMeasurementDialog 
        open={openDialog} 
        onOpenChange={setOpenDialog} 
        onSubmit={handleSubmit} 
        defaultValues={defaultValues} 
        isEditing={!!editingMeasurement} 
        availableProducts={products} 
      />
    </div>
  );
}
